import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { createCaseStudy } from '../../../service/case-studies';
import CustomToast from '../../Toast';
import CaseStudyHeader from '../../layouts/caseStudyHeader';
import {getCaseStudyDetails, publishCaseStudies } from '../../../service/case-studies';
import apiFunctions from '../../../service/axiosConfig/apiFunctions';
import { baseUrl } from '../../../constants';
import { useSearchParams } from 'react-router-dom';

const CreateCaseStudies = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [year, setYear] = useState();
  const [tags, setTags] = useState([]);
  const [content, setContent] = useState('');
  const [thumbnail, setThumbnail] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [upload, setUpload] = useState(false);
  const [show, setShow] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState([]);
  const [loader, setLoader] = useState(false);
  const [caseStudyId, setCaseStudyId] = useState(null); 
  const [caseStudyCreated, setCaseStudyCreated] = useState(false);
  const [file_path, setFile_path] = useState('');
  const [status, setStatus] = useState('draft'); 

  // MyUploadAdapter.js
class MyUploadAdapter {
    constructor(loader, onFileUpload) {
        this.loader = loader;
        this.onFileUpload = onFileUpload;
    }
    upload() {
        return this.loader.file
            .then((file) => {
                // Implement your file upload logic here
                // For demonstration purposes, log the file name and size
                // Call the function passed to the constructor
                this.onFileUpload(file);

                return new Promise((resolve, reject) => {
                    resolve({ file });
                });
            })
            .catch((error) => {
                console.error('Error uploading file:', error);
                CustomToast.error('Error uploading file');
                return Promise.reject('Error uploading file');
            });
    }
}

// Helper function to upload images
 const handleContentImageUpload = async (e, name, i, oi) => {
        if (e) {
            try {
                // Calling the new function to upload the rating
                const result = await apiFunctions.uploadImage(`${baseUrl}uploadImage`, e);
                await setFile_path(result.data.file)
                // Update the courseContent with the image URL
                if (name == "content") {
                    setContent((prevContent) => {
                        return prevContent.replace(/<figure class="image"><img><\/figure>/g, `<figure className="image"><img src="${result.data.file}"></figure>`);
                    });
                }
                
                return result.data.file_path;
            } catch (error) {
                console.error('Image upload error:', error);
                CustomToast.error('Error while uploading file');
            }

        }
        else {
            CustomToast.error('Please select an image');
            return;
        }

    };

 const handleAdditionalEditorChange = (data) => {
        setContent(data);
  };

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      setCaseStudyId(id);
      setCaseStudyCreated(true);
      fetchCaseStudyDetails(id);
    }
  }, [searchParams]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    console.log("Selected files:", file);
  };

  const handleImageUpload = async () => {
    try {
      setLoader(true);
      const response = await apiFunctions.uploadDocFiles(`${baseUrl}uploadFiles`, selectedFile);
      const uploadedUrls = response.data?.urls;

      setUploadedUrl(uploadedUrls);
      setPreviewUrl(selectedFile);

      CustomToast.success("Files uploaded successfully!");

    } catch (error) {
      console.error("Upload error:", error);
      CustomToast.error("Failed to upload files.");
    } finally {
      setLoader(false);
    }
  };

  const handleRemoveFile = () => {
    setUploadedUrl([]);
    setSelectedFile(null);
    setPreviewUrl(null);
    CustomToast.success("Image removed successfully!");
  };

  const handleSaveOrPublish = async (type) => {
    setIsSaving(true);

    const payload = {
      title,
      subtitle,
      year: (year ? Number(year) : null),
      tags,
      case_study: content,
      upload_url: uploadedUrl || previewUrl || [],
      status: type,
    };

    try {
      const res = await createCaseStudy(payload);

      if (res.success && res?.data?.status) {
        if (res?.data?._id) {
          setCaseStudyId(res.data._id);
          setCaseStudyCreated(true);
          setStatus(type); 
          setSearchParams({ id: res.data._id });
          
          fetchCaseStudyDetails(res.data._id);
        }
        CustomToast.success("Case Study saved successfully");
      } else {
        handleValidationMessages(res.message);
      }
    } catch (err) {
      CustomToast.error("Unexpected error occurred.");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

   const fetchCaseStudyDetails = async (caseStudyId) => {
        try {
            const res = await getCaseStudyDetails(caseStudyId);
            const data = res?.data;

            setTitle(data.title || '');
            setSubtitle(data.subtitle || '');
            setYear(data.year || null);
            setTags(data.tags || []);
            setContent(data.case_study || '');
            setPreviewUrl(data.thumbnail || data.upload_url || []);
            setStatus(data.status || 'draft'); 
            setUploadedUrl(data.upload_url || []);
        } catch (error) {
            CustomToast.error("Failed to load case study.");
        }
    };

  const handleValidationMessages = (message) => {
    if (!message) {
      CustomToast.error('Unexpected error occurred.');
      return;
    }

     if (message.includes('"title" is not allowed to be empty')) {
      CustomToast.error('Title is required.');
    } else if (message.includes('"subtitle" is not allowed to be empty')) {
      CustomToast.error('Subtitle is required.');
    } else if (message.includes('"case_study" is not allowed to be empty')) {
      CustomToast.error('Case Study content is required.');
    } else if (message.includes('"Year must be a number') || message.includes('"year" must be a number') || year === '' || year === null) {
      CustomToast.error('Year must be a number');
    } else if (message.includes('"At least one tag is required') || message.includes('"tags" is not allowed to be empty') || tags?.length === 0) {
      CustomToast.error('At least one tag is required');
    } else if (message.includes('At least one file must be uploaded')) {
      CustomToast.error('At least one file must be uploaded.');
    } else {
      CustomToast.error('Error occurred while saving the Case Study.');
    }
  };

  const handleTogglePublishStatus = async () => {
    setIsPublishing(true);

    try {
      if (!caseStudyCreated) {
        CustomToast.error("Please save the case study first before changing status.");
        return;
      }

      // Toggle between published and draft
      const newStatus = status === 'published' ? 'draft' : 'published';
      const response = await handleUpdateCaseStudyStatus(caseStudyId, newStatus);

      if (response?.data?.status) {
        setStatus(newStatus);
        const actionMessage = newStatus === 'published' ? 'published' : 'moved to draft';
        CustomToast.success(`Case Study ${actionMessage} successfully`);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleUpdateCaseStudyStatus = async (caseStudyId, newStatus) => {
    const data = {
      is_publish: newStatus === 'published',
      publish_date: new Date().toISOString().split('T')[0],
      status: newStatus
    };

    try {
      const response = await publishCaseStudies(caseStudyId, data);
      return response;
    } catch (error) {
      CustomToast.error(`Error while updating Case Study status`);
      return error;
    }
  };


  return (
    <>
      <CaseStudyHeader
        isEdit={false}
        isSaving={isSaving}
        isPublishing={isPublishing}
        onSaveDraft={() => handleSaveOrPublish("draft")}
        onPublish={handleTogglePublishStatus}
        isSaveDisabled={false}
        caseStudyCreated={caseStudyCreated}
        currentStatus={status}
      />

      {/* Sidebar */}
      <aside className={`course_details_sidebar sidebar px-4 pt-5 ${show ? "review" : ""}`} id="admin-dash-nav">
        <div className='d-flex flex-column justify-content-between h-100 p-3 course_details'>
          <div className='row'>
            <h5 className='font-20 mb-4'>Case Study Thumbnail</h5>
            <div className='d-flex justify-content-between align-items-start position-relative '>

              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ maxWidth: '240px' }}
              />
              {(selectedFile || uploadedUrl?.length > 0) && (
                <button type='button' onClick={handleRemoveFile} className='position-absolute' style={{ right: '0px', padding: '0px 5px 3px', width: '30px' }}>
                  <img src="/images/delete.png" alt="remove" className="remove-pic" />
                </button>
              )}
            </div>
            {uploadedUrl?.length > 0 ? (
              <img src={uploadedUrl[0]} alt="Preview" className='my-4' style={{ maxWidth: '100%', maxHeight: '200px' }} />
            ) : selectedFile ? (
              <img src={URL.createObjectURL(selectedFile)} alt="Preview" className='my-4' style={{ maxWidth: '100%', maxHeight: '200px' }} />
            ) : (
              <img src="/images/image.png" alt="placeholder" className='my-4' />
            )}
            <button type='button' className='mt-4 btn btn-primary px-4  w-75 mx-auto' onClick={handleImageUpload}>Upload</button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className='course_main'>
        <div className="container-fluid">
          <form onSubmit={handleSaveOrPublish}>
            {/* Title */}
            <div className="mb-3">
              <label className="form-label fw-bold">Case Study Title</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />

            </div>

            {/* Subtitle */}
            <div className="mb-3">
              <label className="form-label fw-bold">Subtitle Title</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter subtitle"
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
              />

            </div>

            {/* Year */}
            <div className="mb-3">
              <label className="form-label fw-bold">Year</label>
              <input
                type="number"
                className="form-control"
                placeholder="Enter year"
                value={year}
                onChange={(e) => setYear(e.target.value)}
               
              />

            </div>

            {/* Tag Input */}
            <div className="mb-3">
              <label className="form-label fw-bold">Add Tag</label>
              <input
                type="text"
                className="form-control"
                placeholder="Press Enter to add tag"
                onKeyDown={(e) => {
                  const value = e.target.value.trim();
                  const isOnlyNumbers = /^\d+$/.test(value); // Regex to check if it's only digits

                  if (e.key === 'Enter' && value !== '') {
                    e.preventDefault();

                    if (!isOnlyNumbers) {
                      setTags([...tags, value]);
                    } else {
                      CustomToast.error('Numeric tags are not allowed.');
                    }

                    e.target.value = '';
                  }
                }}
              
              />

              <div className="mt-2">
                {tags?.map((tag, idx) => (
                  <span key={idx} className="badge bg-secondary me-2 mb-2 d-inline-flex align-items-center">
                    {tag}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      style={{ fontSize: '0.525rem', color: '#fff', opacity: 1, boxShadow: 'none' }}
                      onClick={() => {
                        const updatedTags = tags.filter((_, index) => index !== idx);
                        setTags(updatedTags);
                      }}
                      aria-label="Remove tag"
                    ></button>
                  </span>
                ))}
              </div>
            </div>


            {/* Case Study CKEditor */}
            <div className="mb-3">
              <label className="form-label fw-bold">Case Study</label>

              <CKEditor
                                                     editor={ClassicEditor}
                                                     data={content || ''}
                                                     name="content"
                                                     onReady={(editor) => {
                                                         try {
                                                             console.log("content", content)
                                                             editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
                                                                 return new MyUploadAdapter(loader, (e) => handleContentImageUpload(e, "content"));
                                                             };
                                                         } catch (error) {
                                                            // handleEditorError(error);
                                                         }
                                                     }}
                                                     //onError={handleEditorError}
                                                     onChange={(event, editor) => handleAdditionalEditorChange(editor.getData())}
                                                 />
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateCaseStudies;

